import type { GuardarFicha, SubPartidaListado, TipoBloqueo, TipoValor } from '@adelante/shared';
import * as XLSX from 'xlsx';

/**
 * Importa fichas de control desde Excel (formato docs/calidad/fichas-control-fase1.xlsx,
 * hoja "Puntos"). Cada combinación Ficha + Tipo es una ficha de ámbito vivienda:
 *
 *   Ficha (subpartida de colado) | Tipo | Sección | Subpartida revisada | Punto de control |
 *   Criterio de aceptación | Obligatorio | Requiere foto | Tipo valor | Unidad | Mín | Máx
 *
 * Columna opcional "Bloqueo" (inspecciones): Al iniciar | Al completar | No bloquea
 * (default Al iniciar; se toma de la primera fila de cada ficha).
 * Las subpartidas se escriben "código nombre" (ej. "1.1.4 Colado Losa"); se usa el código.
 * Código de ficha generado: INSP-<código> / EVAL-<código>.
 */

export interface FichaImportada {
  ficha: GuardarFicha;
  subPartidaCodigo: string;
}

export interface ResultadoImportacion {
  fichas: FichaImportada[];
  errores: string[];
}

const COL = {
  ficha: 'Ficha (subpartida de colado)',
  tipo: 'Tipo',
  seccion: 'Sección',
  revisada: 'Subpartida revisada',
  punto: 'Punto de control',
  criterio: 'Criterio de aceptación',
  obligatorio: 'Obligatorio',
  foto: 'Requiere foto',
  tipoValor: 'Tipo valor',
  unidad: 'Unidad',
  min: 'Mín',
  max: 'Máx',
  bloqueo: 'Bloqueo',
} as const;

const texto = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());
const codigoDe = (v: unknown) => texto(v).split(/\s+/)[0] ?? '';
const siNo = (v: unknown) => /^s[ií]$/i.test(texto(v));
const numero = (v: unknown): number | null => {
  const t = texto(v).replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isNaN(n) ? null : n;
};
function bloqueoDe(v: unknown): TipoBloqueo {
  const t = texto(v).toLowerCase();
  if (t.includes('complet')) return 'completar';
  if (t.startsWith('no') || t.includes('ningun')) return 'ninguno';
  return 'inicio';
}

const TIPO_VALOR: Record<string, TipoValor> = {
  ninguno: 'ninguno',
  valor: 'valor',
  calificacion: 'calificacion',
  calificación: 'calificacion',
};

export async function leerExcelFichas(
  archivo: File,
  subPartidas: SubPartidaListado[],
): Promise<ResultadoImportacion> {
  const wb = XLSX.read(await archivo.arrayBuffer(), { type: 'array' });
  const hoja = wb.Sheets.Puntos ?? wb.Sheets[wb.SheetNames[0] ?? ''];
  if (!hoja) return { fichas: [], errores: ['El archivo no tiene la hoja "Puntos"'] };
  // La columna de la ficha puede llamarse "Ficha (subpartida de colado)" o
  // "Ficha (sub-partida)": se toma la primera que empiece con "Ficha".
  const filas = XLSX.utils
    .sheet_to_json<Record<string, unknown>>(hoja, { defval: null })
    .map((f) => {
      const k = Object.keys(f).find((x) => x.trim().startsWith('Ficha'));
      return k && k !== COL.ficha ? { ...f, [COL.ficha]: f[k] } : f;
    });

  const porCodigo = new Map(subPartidas.map((s) => [s.codigo, s]));
  const errores: string[] = [];
  const fichas = new Map<string, FichaImportada>();

  filas.forEach((f, i) => {
    const fila = i + 2; // encabezado en la fila 1
    const puntoTxt = texto(f[COL.punto]);
    if (!puntoTxt) return; // filas vacías
    const subCod = codigoDe(f[COL.ficha]);
    const sub = porCodigo.get(subCod);
    if (!sub) {
      errores.push(`Fila ${fila}: subpartida "${texto(f[COL.ficha])}" no existe en el catálogo`);
      return;
    }
    const tipoTxt = texto(f[COL.tipo]).toLowerCase();
    const tipo = tipoTxt.startsWith('insp')
      ? 'inspeccion'
      : tipoTxt.startsWith('eval')
        ? 'evaluacion'
        : null;
    if (!tipo) {
      errores.push(`Fila ${fila}: tipo "${texto(f[COL.tipo])}" debe ser Inspección o Evaluación`);
      return;
    }
    const revCod = codigoDe(f[COL.revisada]);
    const revisada = revCod ? porCodigo.get(revCod) : undefined;
    if (revCod && !revisada)
      errores.push(
        `Fila ${fila}: subpartida revisada "${texto(f[COL.revisada])}" no existe (se omite)`,
      );
    const tv = TIPO_VALOR[texto(f[COL.tipoValor]).toLowerCase() || 'ninguno'];
    if (!tv) {
      errores.push(`Fila ${fila}: tipo valor "${texto(f[COL.tipoValor])}" no válido`);
      return;
    }
    const min = numero(f[COL.min]);
    const max = numero(f[COL.max]);
    if (min !== null && max !== null && min > max) {
      errores.push(`Fila ${fila}: Mín es mayor que Máx`);
      return;
    }

    const clave = `${tipo}|${sub.codigo}`;
    let item = fichas.get(clave);
    if (!item) {
      const prefijo = tipo === 'inspeccion' ? 'INSP' : 'EVAL';
      item = {
        subPartidaCodigo: sub.codigo,
        ficha: {
          codigo: `${prefijo}-${sub.codigo}`,
          nombre: `${tipo === 'inspeccion' ? 'Inspección' : 'Evaluación'} ${sub.nombre}`,
          categoria: 'calidad_constructiva',
          tipo,
          ambito: 'vivienda',
          bloqueo: tipo === 'inspeccion' ? bloqueoDe(f[COL.bloqueo]) : 'ninguno',
          activo: true,
          sub_partida_ids: [sub.id],
          secciones: [],
        },
      };
      fichas.set(clave, item);
    }
    const nombreSec = texto(f[COL.seccion]) || 'General';
    let sec = item.ficha.secciones.find((s) => s.nombre === nombreSec);
    if (!sec) {
      sec = { nombre: nombreSec, sub_partida_revisada_id: revisada?.id ?? null, puntos: [] };
      item.ficha.secciones.push(sec);
    }
    sec.puntos.push({
      texto: puntoTxt,
      criterio: texto(f[COL.criterio]) || null,
      obligatorio: siNo(f[COL.obligatorio]),
      requiere_foto: siNo(f[COL.foto]),
      tipo_valor: tv,
      unidad: tv === 'valor' ? texto(f[COL.unidad]) || null : null,
      valor_min: tv === 'valor' ? min : null,
      valor_max: tv === 'valor' ? max : null,
    });
  });

  return { fichas: [...fichas.values()], errores };
}
